import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import Tile from './Tile';
import FeatureHeader from './FeatureHeader';
import SharedBuffySprite from '../buffy/BuffySprite';
import { SLOW, useMedia, useReveal } from './useReveal';
import { PLACEHOLDER, PLACEHOLDER_SHORT } from '../storyScript';

/* F3 "Ask Buffy. See why." (PAGE STRUCTURE v3: a feature section, text on
   the left), as the owner's "talk with Buffy video in a box" (2026-09-29):
   an auto-playing, looping conversation inside a fixed-height white tile, in
   OUR product's chat look (the teacher's words in the soft #f3f5ef pill, in
   ink; Buffy's plain replies plus one white card; the 64px composer, its
   send button the hero's). TWO exchanges (SAMPLE DATA CANON, DECISIONS.md):
   who is stuck and on what (3 students, Advanced Math: factoring
   quadratics), then why (the example left, Buffy's explanation right).
   Assigning lives in F4. Each question types into the composer (ink),
   sends, Buffy thinks, then answers; messages push up like a real chat (a
   transform-only slide); then it clears and loops (about 15s).

   The whole chat is a pure function of one clock `t` (frameAt), so pausing
   off screen is just stopping the clock, and reduced motion / ?settled=1 is
   the final frame drawn as a static transcript (both exchanges, the box
   grows to fit). Buffy stands beside the composer, feet on its line (192px
   from 1280, 128 below): in the lane LEFT of the chat from 1264, beside its
   RIGHT edge when the features stack (900-1263, the chat on the page's left
   edge), on the tile's top edge under 900. He gestures on his replies:
   point at the first answer (only from the left lane; elsewhere a point
   would aim away from the chat, so he encourages), think while "Buffy is
   thinking" about why. Students are initials only (Buffy is the only
   character). */

const MINUS = '−';
const EXCHANGES = [
  { q: 'Who is stuck, and on what?', reply: '3 students are stuck on Advanced Math: factoring quadratics.', card: 'miss', gesture: 'point', at: 'reply', hold: 2600 },
  // owner 2026-09-29: the "why" answer is ONE card, the example on the left and
  // Buffy's explanation on the right (reply = the screen-reader text only)
  { q: 'Why?', reply: `They picked x = ${MINUS}2 and ${MINUS}3. They factored it right but flipped the signs. (x ${MINUS} 2)(x ${MINUS} 3) = 0 gives x = 2 and 3.`, card: 'why', gesture: 'think', at: 'sent', hold: 4200 },
];

// base ms; the clock runs SLOW (1.35x) slower than these numbers
const TYPE = 34;
const SEND = 320;
const THINK = 900;
const CARD = 480;
const GAP = 250;
const CLEAR = 700;

// times get their own names (tStart, ...) so they never overwrite the
// exchange's text (reply) or card kind (card)
function buildTimeline() {
  let t = 500;
  const ex = EXCHANGES.map((e) => {
    const tStart = t;
    const tSent = tStart + e.q.length * TYPE + SEND;
    const tReply = tSent + THINK;
    const tCard = e.card === 'why' ? tReply : tReply + CARD;
    const tEnd = tCard + e.hold;
    t = tEnd + GAP;
    return { ...e, tStart, tSent, tReply, tCard, tEnd };
  });
  return { ex, clear: t, loop: t + CLEAR };
}

function frameAt(T, t) {
  const items = [];
  let typed = '';
  let typing = false;
  let cue = null;
  T.ex.forEach((e, i) => {
    if (t >= e.tStart && t < e.tSent) {
      typing = true;
      typed = e.q.slice(0, Math.min(e.q.length, Math.floor((t - e.tStart) / TYPE)));
    }
    if (t >= e.tSent) items.push({ id: `q${i}`, kind: 'you', text: e.q });
    if (t >= e.tSent && t < e.tReply) items.push({ id: `t${i}`, kind: 'thinking' });
    if (t >= e.tReply && e.card !== 'why') items.push({ id: `r${i}`, kind: 'buffy', text: e.reply });
    if (t >= e.tCard) items.push({ id: `c${i}`, kind: e.card });
    const cueAt = e.at === 'sent' ? e.tSent : e.tReply;
    if (t >= cueAt) cue = { clip: e.gesture, id: i };
  });
  return { items, typed, typing, clearing: t >= T.clear, cue };
}

function frameKey(f) {
  return `${f.items.map((x) => x.id).join(',')}|${f.typed}|${f.typing}|${f.clearing}|${f.cue ? f.cue.id : '-'}`;
}

function Ini({ t, fill }) {
  return <span className="cxs-ini cxs-ini-sm" style={{ background: fill }}>{t}</span>;
}

function Card({ kind }) {
  if (kind === 'miss') {
    return (
      <div className="cx-surface cxs-chat-card">
        <span className="cxs-ab-label">What they miss</span>
        <span className="cxs-ab-val">
          <span className="cx-chip cxs-chip cxs-chip-line">
            <span className="cx-chip-dot" style={{ background: '#3B82F6' }} />
            Advanced Math
          </span>
          <span className="cxs-stack">
            <Ini t="AL" fill="#C8F3DD" />
            <Ini t="SN" fill="#FFD6DC" />
            <Ini t="DP" fill="#DCD5FF" />
          </span>
        </span>
      </div>
    );
  }
  // x^2 - 5x + 6 = 0 is (x - 2)(x - 3) = 0, so x = 2 and 3; the three
  // students wrote -2 and -3 (the signs flipped)
  return (
    <div className="cx-surface cxs-chat-card cxs-chat-why">
      <div className="cxs-why-half">
        <span className="cxs-why-label">The question</span>
        <div className="cxs-why-stem">x² {MINUS} 5x + 6 = 0</div>
        <div className="cxs-why-choices">
          <div className="cxs-why-choice is-picked">
            <span className="cxs-why-val">x = {MINUS}2 and {MINUS}3</span>
            <span className="cxs-why-tag">They picked</span>
          </div>
        </div>
      </div>
      <div className="cxs-why-half cxs-why-right">
        <span className="cxs-why-label">Why</span>
        <p className="cxs-why-text">They factored it right but flipped the signs.</p>
        <div className="cxs-why-work">
          <span>(x <mark className="cxs-chat-hl">{MINUS} 2</mark>)(x <mark className="cxs-chat-hl">{MINUS} 3</mark>) = 0</span>
        </div>
        <div className="cxs-why-work">
          <span>gives x = 2 and 3</span>
        </div>
      </div>
    </div>
  );
}

function Item({ it }) {
  if (it.kind === 'you') return <div className="cxs-msg cxs-msg-you">{it.text}</div>;
  if (it.kind === 'thinking') {
    return (
      <div className="cxs-msg cxs-msg-think">
        <span className="cxs-dots" aria-hidden="true"><i /><i /><i /></span>
        Buffy is thinking
      </div>
    );
  }
  if (it.kind === 'buffy') return <p className="cxs-msg cxs-msg-buffy">{it.text}</p>;
  return <div className="cxs-msg cxs-msg-card"><Card kind={it.kind} /></div>;
}

/* Buffy on the shared player, gesturing on the chat's cues; between cues he
   breathes (idle-breathe loops and may leave on any frame: all are adjacent
   to rest, the first frame of every gesture). `side` = he stands beside the
   tile, so a point aims into the chat; otherwise 'point' plays 'encourage'. */
function ChatBuffy({ size, cue, paused, still, side, className }) {
  const [clip, setClip] = useState('idle-breathe');
  const [run, setRun] = useState(0);
  const pending = useRef(null);
  const cueId = cue ? `${cue.id}` : null;
  useEffect(() => {
    if (!cue || still) return;
    const next = cue.clip === 'point' && !side ? 'encourage' : cue.clip;
    if (clip === 'idle-breathe') {
      setClip(next);
      setRun((r) => r + 1);
    } else {
      pending.current = next;
    }
    // the cue id is the trigger; clip is read, not a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cueId, still]);
  const onEnd = () => {
    const next = pending.current || 'idle-breathe';
    pending.current = null;
    setClip(next);
    setRun((r) => r + 1);
  };
  const idle = clip === 'idle-breathe';
  return (
    <span className={`cxs-buffy ${className}`} style={{ width: size, height: size }} aria-hidden="true">
      <SharedBuffySprite
        clip={still ? 'rest' : clip}
        scale={Math.round(size / 64)}
        paused={paused || still}
        loop={still ? false : idle}
        run={run}
        trim={!idle}
        onEnd={still || idle ? undefined : onEnd}
      />
    </span>
  );
}

function useChatClock(T, playing, still, paused) {
  const [frame, setFrame] = useState(() => (still ? frameAt(T, T.clear - 1) : frameAt(T, 0)));
  const clock = useRef(0);
  // the pause button during the clear (the log fading out) holds the full
  // conversation instead of a half-faded one; play resumes from there
  useEffect(() => {
    if (!paused || still || clock.current < T.clear) return;
    clock.current = T.clear - 1;
    setFrame(frameAt(T, T.clear - 1));
  }, [T, paused, still]);
  useEffect(() => {
    if (still) {
      setFrame(frameAt(T, T.clear - 1));
      return undefined;
    }
    if (!playing) return undefined;
    let raf = 0;
    let last = performance.now();
    let key = '';
    const tick = (now) => {
      clock.current += (now - last) / SLOW;
      last = now;
      if (clock.current >= T.loop) clock.current = 0;
      const f = frameAt(T, clock.current);
      const k = frameKey(f);
      if (k !== key) {
        key = k;
        setFrame(f);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [T, playing, still]);
  return frame;
}

/* New messages push the log up: bottom-anchored, and each height change is
   played as a slide (FLIP, transform only). */
function useChatScroll(ref, itemsKey, still) {
  const prevH = useRef(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || still) return;
    const h = el.offsetHeight;
    const delta = h - prevH.current;
    prevH.current = h;
    if (!delta || h === 0) return;
    el.style.transition = 'none';
    el.style.transform = `translate3d(0, ${delta}px, 0)`;
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;
    el.style.transition = `transform ${Math.round(460 * SLOW)}ms cubic-bezier(.2, .8, .2, 1)`;
    el.style.transform = 'translate3d(0, 0, 0)';
  }, [ref, itemsKey, still]);
}

/* The scene alone (schools page 2026-10-01: its feature tabs show it
   without this section's heading). */
export function AskBuffyScene() {
  const { ref, reveal, live, still } = useReveal({ amount: 0.4 });
  // 900px+: Buffy stands beside the tile, on its bottom line, next to the
  // composer (as in the product); narrower, on the tile's top edge
  const side = useMedia('(min-width: 900px)');
  const big = useMedia('(min-width: 1280px)');
  // under 340 px the full placeholder is wider than the composer (critique #10)
  const tiny = useMedia('(max-width: 339px)');
  const size = side && big ? 192 : 128;
  // 1264+: text and chat side by side, Buffy in the lane LEFT of the chat.
  // 900-1263 (stacked, the chat on the page's left edge, final formatting
  // #5): he stands beside the chat's RIGHT edge; a point would aim away from
  // the chat there, so he encourages instead
  const lane = useMedia('(min-width: 1264px)');
  const onRight = side && !lane;

  const T = useMemo(buildTimeline, []);
  const playing = reveal === 'play' && live;
  const f = useChatClock(T, playing, still, false);
  const logRef = useRef(null);
  useChatScroll(logRef, f.items.map((x) => x.id).join(','), still);

  return (
    <div ref={ref} className="cxs-ask-stage" data-reveal={reveal} data-live={live ? 'true' : 'false'}>
      <Tile look="site" canvas="white" className={`cxs-ask-tile${still ? ' is-still' : ''}`} aria-hidden="true">
        <div className="cxs-chat">
          <div className={`cxs-chat-log${f.clearing ? ' is-clearing' : ''}`}>
            <div ref={logRef} className="cxs-chat-inner">
              {f.items.map((it) => <Item key={it.id} it={it} />)}
            </div>
          </div>
          <div className="cx-surface cxs-ab-composer">
            <span className={`cxs-ab-input${f.typing ? '' : ' is-empty'}`}>
              {f.typing ? (
                <>
                  {f.typed}
                  <span className="cxs-caret" aria-hidden="true" />
                </>
              ) : (tiny ? PLACEHOLDER_SHORT : PLACEHOLDER)}
            </span>
            <span className={`cxs-ab-send${f.typing && f.typed ? ' is-on' : ''}`}><ArrowUp strokeWidth={2.2} /></span>
          </div>
        </div>
      </Tile>
      {/* the conversation, for screen readers */}
      <ol className="cxs-sr">
        {EXCHANGES.map((e) => (
          <li key={e.q}>{`Teacher: ${e.q} Buffy: ${e.reply}`}</li>
        ))}
      </ol>
      <ChatBuffy
        className={`cxs-ask-buffy ${side ? 'is-side' : 'is-top'}${onRight ? ' is-right' : ''} is-${size}`}
        size={size}
        cue={f.cue}
        paused={!live}
        still={still}
        side={side && !onRight}
      />
    </div>
  );
}

/* `header` (schools preview, 2026-09-30): optional FeatureHeader overrides
   (title, sub, side, id, as, labelled, after, className added to ours) so the
   schools page can reuse this scene under its own words. With no props the
   teacher page renders exactly as before. */
export default function AskBuffy({ header = {} }) {
  return (
    <FeatureHeader title={(<><span>Ask Buffy.</span>{' '}<span className="cxs-h2-tail">See why.</span></>)} sub="See who is stuck, on what, and why." side="left" id="cxs-f3" {...header} className={`cxs-feature-ask${header.className ? ` ${header.className}` : ''}`}>
      <AskBuffyScene />
    </FeatureHeader>
  );
}
